/**
 * @fileoverview Section "Dialogs" — Dialog, AlertDialog, Sheet, Popover.
 *
 * Illustre les 4 patrons de contenu superposé. Chacun a un usage précis :
 *   - **Dialog** : formulaire ou tâche focalisée, l'utilisateur doit finir
 *     ou annuler explicitement. Ex : `ReplayDialog`, `RefundRequestDialog`.
 *   - **AlertDialog** : confirmation destructive irréversible.
 *     Ex : `RefundApprovalDialog`, révocation session.
 *   - **Sheet** : drawer latéral, tâche secondaire tout en gardant le
 *     contexte principal visible. Ex : détail paiement, filtres avancés.
 *   - **Popover** : petit contenu contextuel non modal. Ex : sélecteur
 *     de date, mini-tooltip enrichi.
 */

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
  Button,
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  FormField,
  Input,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@gnpc/ui';
import { CalendarDays } from 'lucide-react';
import { DsPreview } from '../components/DsPreview.tsx';

export function DsSectionDialogs(): JSX.Element {
  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <DsPreview label="Dialog — formulaire de rejeu STI">
        <Dialog>
          <DialogTrigger asChild>
            <Button>Ouvrir le dialog</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Rejeu manuel — PAY-2947</DialogTitle>
              <DialogDescription>
                Cette action déclenche un nouvel appel <code>EnregistrerPaiement</code> vers
                le STI. Motif obligatoire (≥ 10 caractères), journalisé.
              </DialogDescription>
            </DialogHeader>
            <FormField
              label="Motif du rejeu"
              required
              tooltip="Journalisé avec votre identité et la capacité exercée."
              hint="10 caractères minimum."
            >
              <Input placeholder="ex : Timeout STI après paiement confirmé." />
            </FormField>
            <DialogFooter>
              <DialogClose asChild>
                <Button variant="outline">Annuler</Button>
              </DialogClose>
              <Button>Rejouer l'enregistrement</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </DsPreview>

      <DsPreview label="AlertDialog — confirmation destructive (session)">
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button variant="destructive">Révoquer la session</Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Révoquer la session agent ?</AlertDialogTitle>
              <AlertDialogDescription>
                L'agent sera déconnecté immédiatement et devra ressaisir son mot de passe
                + MFA. Action journalisée. Cette opération est irréversible.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Annuler</AlertDialogCancel>
              <AlertDialogAction className="bg-destructive text-white hover:bg-destructive/90">
                Révoquer
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </DsPreview>

      <DsPreview label="Sheet — drawer latéral (filtres avancés)">
        <Sheet>
          <SheetTrigger asChild>
            <Button variant="outline">Ouvrir les filtres</Button>
          </SheetTrigger>
          <SheetContent>
            <SheetHeader>
              <SheetTitle>Filtres avancés — paiements</SheetTitle>
              <SheetDescription>
                Combiner les filtres pour affiner la vue. Les résultats se mettent à jour
                en direct.
              </SheetDescription>
            </SheetHeader>
            <div className="grid gap-4 px-6 py-4">
              <FormField label="De" hint="Date d'initiation">
                <Input type="date" />
              </FormField>
              <FormField label="À" hint="Date d'initiation">
                <Input type="date" />
              </FormField>
            </div>
            <SheetFooter>
              <Button variant="outline">Réinitialiser</Button>
              <Button>Appliquer</Button>
            </SheetFooter>
          </SheetContent>
        </Sheet>
      </DsPreview>

      <DsPreview label="Popover — contenu contextuel non modal">
        <Popover>
          <PopoverTrigger asChild>
            <Button variant="outline">
              <CalendarDays />
              Sélectionner une date
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-64">
            <div className="space-y-2 text-sm">
              <p className="font-medium text-quipux-navy-950">Sélecteur de date</p>
              <p className="text-xs text-muted-foreground">
                Un DatePicker viendra ici une fois livré. Le Popover fournit le
                positionnement et l'accessibilité (focus trap, esc, click-outside).
              </p>
            </div>
          </PopoverContent>
        </Popover>
      </DsPreview>
    </div>
  );
}
